import { useRouter } from 'next/router';

import { useStrippedRouter } from '@pflegia/ui/hooks/use-stripped-router';
import {
  APPLICATIONS,
  EMPLOYERS,
  JOB,
  JOB_PREVIEW,
  OFFERS,
  SALARY_CALCULATOR,
  SEO_LANDING_PAGE_ROOT,
} from '@pflegia/ui/pflegia/constants/routes';
import { isDefined } from '@pflegia/util/functions/type-guards';

import { NOT_FOUND_ROUTE_QUERY } from '@shared/constants/job-offer';

/**
 * Main routing hook for the main-frontend app.
 * Extends useStrippedRouter with app-specific query parameter checks.
 *
 * @returns Object containing:
 * - All properties from useStrippedRouter (pathname, asPath, route type flags)
 * - App-specific flags based on query parameters (isPreview, isSlimmed, etc.)
 *
 * @note The functionality of this hook is to decide the properties of the current path.
 */
export const useRouterHelper = () => {
  const router = useRouter();
  const strippedRouter = useStrippedRouter();

  const {
    query: { preview, accessPoint, source, slimmed },
  } = router;

  const { pathname } = strippedRouter;

  const isJobPage = (strippedRouter.isJobPage || strippedRouter.isJobDetailsPage) && !strippedRouter.isJobsPage;

  return {
    pathname: strippedRouter.pathname,
    asPath: strippedRouter.asPath,
    isJobDetailsPage: strippedRouter.isJobDetailsPage,
    isJobBoard: strippedRouter.isJobBoardPage,
    isJobPage,
    isExternalJob: strippedRouter.isExternalJobPage,
    isTransparentPath: strippedRouter.isJobsPage || strippedRouter.isExternalJobPage || strippedRouter.isJobDetailsPage,
    isAnyJobPage: strippedRouter.isJobPage || strippedRouter.isJobDetailsPage || strippedRouter.isJobsPage || strippedRouter.isJobBoardPage,
    isTransparentJobBoard: strippedRouter.isJobsPage,
    isTransparentJobDetails: strippedRouter.isJobDetailsPage,
    isEmployersPage: pathname.startsWith(EMPLOYERS),
    isSeoLandingPage: pathname.startsWith(SEO_LANDING_PAGE_ROOT),
    isOffersPage: pathname.startsWith(OFFERS),
    isSalaryCalculatorPage: pathname.startsWith(SALARY_CALCULATOR),
    isJobRoute: pathname.startsWith(`${JOB}/`) || pathname === JOB,
    isBlog: strippedRouter.isBlogPage,
    isPreview: preview === 'true' || pathname === JOB_PREVIEW, // employer page uses query param still
    isTransparentFunnel: isDefined(accessPoint) && accessPoint === 'transparent',
    isMinifiedJobBoard: source === NOT_FOUND_ROUTE_QUERY,
    isSlimmed: isJobPage && slimmed === 'true',
    isApplicationsPage: pathname.startsWith(APPLICATIONS),
  };
};
